'use client';

import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react';

export type DashboardTheme = 'opal' | 'aurora';

type DashboardThemeContextValue = {
  theme: DashboardTheme;
  setTheme: (theme: DashboardTheme) => void;
  toggleTheme: () => void;
};

const DashboardThemeContext = createContext<DashboardThemeContextValue>({
  theme: 'aurora',
  setTheme: () => undefined,
  toggleTheme: () => undefined,
});

export const DASHBOARD_BROWSER_THEME_COLORS: Record<DashboardTheme, string> = {
  aurora: '#06101e',
  opal: '#f4f7f3',
};

export function DashboardThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<DashboardTheme>('aurora');

  const applyThemeToDocument = useCallback((t: DashboardTheme) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', t);
    root.classList.toggle('dark', t === 'aurora');
    root.style.colorScheme = t === 'aurora' ? 'dark' : 'light';
    root.style.backgroundColor = DASHBOARD_BROWSER_THEME_COLORS[t];

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    themeMeta?.setAttribute('content', DASHBOARD_BROWSER_THEME_COLORS[t]);

    const statusBarMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );
    statusBarMeta?.setAttribute('content', t === 'aurora' ? 'black-translucent' : 'default');

    if (document.body) {
      document.body.style.backgroundColor = DASHBOARD_BROWSER_THEME_COLORS[t];
    }
  }, []);

  useLayoutEffect(() => {
    try {
      const saved = localStorage.getItem('ertzyx-dashboard-theme');
      if (saved === 'opal' || saved === 'aurora') {
        setThemeState(saved);
        applyThemeToDocument(saved);
      } else {
        const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
        const initial = prefersLight ? 'opal' : 'aurora';
        setThemeState(initial);
        applyThemeToDocument(initial);
      }
    } catch {
      applyThemeToDocument('aurora');
    }
  }, [applyThemeToDocument]);

  useLayoutEffect(() => {
    applyThemeToDocument(theme);
  }, [theme, applyThemeToDocument]);

  const setTheme = useCallback(
    (t: DashboardTheme) => {
      setThemeState(t);
      applyThemeToDocument(t);
      try {
        localStorage.setItem('ertzyx-dashboard-theme', t);
      } catch {
        /* ignore */
      }
    },
    [applyThemeToDocument]
  );

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'aurora' ? 'opal' : 'aurora';
      try {
        localStorage.setItem('ertzyx-dashboard-theme', next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <DashboardThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </DashboardThemeContext.Provider>
  );
}

export function useDashboardTheme() {
  return useContext(DashboardThemeContext);
}
